// 카탈로그 엔트리 선택 공통 코어 — "카탈로그 entry 하나를 고른다"는 동작의 단일 구현.
// (catalog-picker-unification P2 — 산재한 12곳 선택 UI를 이 코어로 수렴한다.)
//
// 코어가 보장하는 것 4종:
//  ① 검색 콤보(내부는 CatalogCombobox 재사용 — group 헤더·kind 배지·타이핑 필터)
//  ② legacyHidden 기본 제외 — 단 현재 값이 목록 밖(legacyHidden 포함)이면 '기존 값(보존)'
//     그룹으로 덧대 표시 유지(신규 선택에서만 숨김, 기존 참조는 소실·은닉 금지)
//  ③ 그룹 → 라벨 한국어 로케일 정렬(저작자가 읽는 문구 기준 — 배열 선언 순서는 목록에서 못 읽는다)
//  ④ allowClear(선택 해제)
//
// entries는 **주입형**이다 — 코어가 catalog 전역을 직접 읽지 않는다. 호출부가 필터(kind 등)나
// 합성 목록(향후 INJECTED_ROW_SOURCES류)을 만들어 넣는다. 옵션 구성·보존 표기·정렬은 순수 함수
// (buildEntryPickerOptions)로 분리해 spec 대상으로 둔다.

import type { CatalogEntry } from '@safetyhub/shared';
import type { InjectedRowSource } from '@safetyhub/report-composer';
import { CatalogCombobox, type ComboboxOption } from './CatalogCombobox';
import { CatalogParamInlineBlock } from './CatalogParamEditing';

/** 목록 밖/legacyHidden 저장값을 덧대 표시하는 그룹 라벨(BindingEditor 보존 패턴과 동일 문구). */
export const PRESERVED_OPTION_GROUP = '기존 값(보존)';

/** 주입 rows 소스(INJECTED_ROW_SOURCES) 어댑터 entry의 콤보 그룹 라벨. */
export const INJECTED_SOURCE_GROUP = '자동 제공 데이터';

/**
 * InjectedRowSource → CatalogEntry 호환 어댑터(순수) — 주입 rows 소스(평가기준 선택 시 서버
 * 자동 제공)는 카탈로그 목록 밖이라 CatalogEntry 형상이 아니다. 픽커 주입용 최소 형상으로
 * 변환한다(실 API 아님 — endpoint '', source 'other', 선언 fields → returns).
 */
export function injectedRowSourceEntry(s: InjectedRowSource): CatalogEntry {
  return {
    key: s.key,
    kind: 'data',
    group: INJECTED_SOURCE_GROUP,
    label: s.label,
    cardinality: 'list',
    endpoint: '',
    source: 'other',
    returns: s.fields.map((f) => ({ path: f.path, label: f.label, type: f.type })),
  };
}

/** mixed(다중 선택 값 혼합) 상태에서 콤보에 보여줄 placeholder. */
export const MIXED_VALUES_PLACEHOLDER = '(여러 값 혼합 — 선택하면 일괄 적용)';

/** 미지정 시 기본 placeholder(기존 prefill 계열 문구 승계). */
export const DEFAULT_ENTRY_PLACEHOLDER = '데이터 소스 검색·선택…';

/** 그룹·라벨 정렬용 한국어 collator — 모듈 상수(정렬 호출마다 localeCompare 내부 생성 반복 방지). */
const KO_COLLATOR = new Intl.Collator('ko');

/**
 * 주입 entries → 콤보 옵션 목록(순수).
 *
 * - legacyHidden 제외(신규 선택 목록에서 숨김 — 기존 참조는 아래 보존 경로가 표시 유지)
 * - 그룹 → 라벨 한국어 로케일 정렬
 * - 현재 value가 가시 목록 밖이면 '기존 값(보존)' 그룹으로 맨 뒤에 덧댐. entry가 entries에
 *   있으면(legacyHidden) 그 라벨·배지를 쓰고, 아예 없으면 키 + "(목록에 없음 — 보존)" 표기 —
 *   설정이 있는데 콤보가 빈칸이면 "안 보임 = 없음"으로 읽히는 유령 상태가 된다.
 */
export function buildEntryPickerOptions(
  entries: CatalogEntry[],
  value: string | undefined,
): ComboboxOption[] {
  const options: ComboboxOption[] = entries
    .filter((e) => !e.legacyHidden)
    .map((e) => ({ value: e.key, label: e.label, key: e.key, group: e.group, badge: e.kind }))
    .sort(
      (a, b) =>
        KO_COLLATOR.compare(a.group ?? '', b.group ?? '') || KO_COLLATOR.compare(a.label, b.label),
    );
  if (value && !options.some((o) => o.value === value)) {
    const hidden = entries.find((e) => e.key === value);
    options.push({
      value,
      label: hidden?.label ?? `${value} (목록에 없음 — 보존)`,
      key: value,
      group: PRESERVED_OPTION_GROUP,
      ...(hidden ? { badge: hidden.kind } : {}),
    });
  }
  return options;
}

/**
 * 재선택 발화 판정(순수) — 콤보는 native select와 달리 **같은 값 재선택도 발화**해, 호출부의
 * "선택 변경 시 파생 필드 초기화" 부수효과(값/라벨 필드 재유도·fieldMap 초기화·select 경로 리셋
 * 등)가 재선택만으로 저작물을 파괴한다. 코어가 같은 값 재선택을 no-op으로 거른다.
 *
 * 단 mixed(다중 선택 값 혼합)면 같은 값이어도 발화한다 — 혼합 상태에서 그 값 선택은
 * "일괄 적용"이라는 실제 변경이다. ''와 undefined는 같은 미선택으로 취급(호출부 저장값이
 * ''인 채 콤보 해제(undefined)가 와도 무변경).
 */
export function shouldEmitEntryChange(
  next: string | undefined,
  current: string | undefined,
  mixed: boolean,
): boolean {
  if (mixed) return true;
  return (next ?? '') !== (current ?? '');
}

/**
 * mixed 상태 반영 표시값(순수) — 혼합이면 콤보를 미선택으로 그리고 placeholder로 알린다
 * (선택하면 onChange가 일괄 적용). 아니면 값·placeholder 그대로.
 */
export function entryPickerDisplay(args: {
  value: string | undefined;
  mixed?: boolean;
  placeholder?: string;
}): { comboValue: string | undefined; comboPlaceholder: string } {
  if (args.mixed) return { comboValue: undefined, comboPlaceholder: MIXED_VALUES_PLACEHOLDER };
  return {
    comboValue: args.value,
    comboPlaceholder: args.placeholder ?? DEFAULT_ENTRY_PLACEHOLDER,
  };
}

export interface CatalogEntryPickerProps {
  /** 선택 후보 — 호출부가 필터/합성해 **주입**한다(코어는 catalog 전역을 읽지 않는다). */
  entries: CatalogEntry[];
  /** 현재 선택된 entry 키(미선택 undefined). */
  value: string | undefined;
  /**
   * 선택 변경 — entry를 함께 넘겨 호출부 부수효과(자동 키·dataType 유도 등)를 지원한다.
   * 같은 값 재선택은 코어가 no-op으로 거른다(mixed면 일괄 적용이라 발화 — shouldEmitEntryChange).
   * entry가 undefined인 경우는 해제(allowClear) 또는 entries에 **아예 없는** 보존 키(제거·리네임)
   * 선택뿐이다 — legacyHidden entry는 entries에 있으므로 보존 그룹에서 골라도 정상 반환된다.
   */
  onChange: (key: string | undefined, entry: CatalogEntry | undefined) => void;
  /** 드롭다운 상단에 (미선택) 항목을 노출해 값 비우기 허용. */
  allowClear?: boolean;
  /**
   * allowClear 해제 항목의 표시 문구 override(기본 "(미선택)") — 해제의 **의미**가 자리마다
   * 다를 때 명시한다(예: 제안형 콤보 "없음(일반 텍스트 입력)" = 해제하면 일반 텍스트 입력 동작).
   */
  clearLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  /** 다중 선택 편집에서 값이 섞인 상태 — 미선택 표시 + 혼합 placeholder(선택 시 일괄 적용). */
  mixed?: boolean;
  /**
   * 선택된 catalogKey의 파라미터 인라인 블록(CatalogParamInlineBlock)을 자동 렌더(기본 true).
   * 저작 대상 파라미터가 없거나 CatalogParamsEditContext Provider 밖이면 블록 스스로 미렌더.
   */
  paramsBlock?: boolean;
}

/** 카탈로그 엔트리 선택 코어 — 검색 콤보 + (선택 시) 파라미터 인라인 블록. */
export function CatalogEntryPicker({
  entries,
  value,
  onChange,
  allowClear = false,
  clearLabel,
  placeholder,
  disabled = false,
  mixed = false,
  paramsBlock = true,
}: CatalogEntryPickerProps) {
  const { comboValue, comboPlaceholder } = entryPickerDisplay({ value, mixed, placeholder });
  // useMemo 없음 — entries가 호출부에서 대부분 매 렌더 새 배열(filter/map)이라 dep 캐시가
  // 사실상 무효였다. 가벼운 순수 계산이라 매 렌더 수행("캐시 있는 척" 방지, 리뷰 INFO).
  const options = buildEntryPickerOptions(entries, comboValue);
  const findEntry = (key: string | undefined): CatalogEntry | undefined =>
    key ? entries.find((e) => e.key === key) : undefined;
  const selectedEntry = findEntry(comboValue);
  return (
    // 루트에 flex-1 min-w-0 — 호출부 flex 행(라벨 + 픽커)에서 남은 폭 채움 + truncate 허용.
    // 블록 부모에서는 둘 다 무효라 부작용 없음(호출부의 동일 목적 래퍼 div 제거 근거).
    <div className="flex-1 min-w-0 space-y-1">
      <CatalogCombobox
        value={comboValue}
        options={options}
        allowClear={allowClear}
        clearLabel={clearLabel}
        placeholder={comboPlaceholder}
        disabled={disabled}
        onChange={(v) => {
          if (!shouldEmitEntryChange(v, value, mixed)) return;
          onChange(v, findEntry(v));
        }}
      />
      {/* entry가 목록에 없어도(제거·리네임) 유령 지정 해제를 위해 catalogKey만으로 블록에 위임 —
          블록이 unknownParam 행을 그려 해제 수단을 보존한다(BindingEditor 삽입처와 동일 규약). */}
      {paramsBlock && comboValue ? (
        <CatalogParamInlineBlock catalogKey={comboValue} entry={selectedEntry} />
      ) : null}
    </div>
  );
}
